import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Eye, EyeOff } from "lucide-react";

const EMPTY = {
  slug: "",
  title: "",
  category: "EMS",
  locality: "Istres",
  excerpt: "",
  keywords: "",
  cover: "",
  author: "Rosalie",
  read_time: 5,
  content: "",
  published: true,
};

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // post id or "new"
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...p,
      keywords: (p.keywords || []).join(", "),
    });
  };

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };

  const save = async () => {
    const payload = {
      ...form,
      keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
      read_time: parseInt(form.read_time) || 5,
    };
    try {
      if (editing === "new") {
        await api.post("/admin/blog", payload);
        toast.success("Article créé");
      } else {
        await api.patch(`/admin/blog/${editing}`, payload);
        toast.success("Article mis à jour");
      }
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Article supprimé");
    load();
  };

  const togglePublish = async (p) => {
    await api.patch(`/admin/blog/${p.id}`, { published: !p.published });
    toast.success(p.published ? "Dépublié" : "Publié");
    load();
  };

  if (editing) {
    return (
      <div className="p-8 lg:p-10 max-w-5xl">
        <button onClick={() => setEditing(null)} className="text-sm text-fegray hover:text-fegreen mb-6">
          ← Retour à la liste
        </button>
        <h1 className="text-3xl font-bold text-fegray-dark mb-8">
          {editing === "new" ? "Nouvel article" : "Éditer l'article"}
        </h1>

        <div className="fe-card p-6 space-y-4" data-testid="blog-editor">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Slug (URL)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="ex: ems-istres-guide" />
            <Field label="Titre" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
            <Field label="Catégorie" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
            <Field label="Localité" value={form.locality} onChange={(v) => setForm({ ...form, locality: v })} />
            <Field label="Auteur" value={form.author} onChange={(v) => setForm({ ...form, author: v })} />
            <Field label="Temps de lecture (min)" type="number" value={form.read_time} onChange={(v) => setForm({ ...form, read_time: v })} />
          </div>
          <Field label="Image de couverture (URL)" value={form.cover} onChange={(v) => setForm({ ...form, cover: v })} />
          <Field label="Extrait / Meta description (max 160 caractères)" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} multiline />
          <Field label="Mots-clés SEO (séparés par virgules)" value={form.keywords} onChange={(v) => setForm({ ...form, keywords: v })} placeholder="EMS Istres, coach domicile, ..." />
          <Field label="Contenu (markdown : ## titre, - liste, **gras**)" value={form.content} onChange={(v) => setForm({ ...form, content: v })} multiline rows={15} />

          <label className="flex items-center gap-2 text-sm text-fegray">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-fegreen w-4 h-4" />
            Publié (visible sur le site)
          </label>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button onClick={() => setEditing(null)} className="btn-outline-green">Annuler</button>
            <button onClick={save} className="btn-green" data-testid="save-blog-post"><Save className="w-4 h-4" /> Enregistrer</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
        <div>
          <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-2">— Blog</p>
          <h1 className="text-3xl lg:text-4xl font-bold text-fegray-dark">Articles SEO</h1>
        </div>
        <button onClick={startNew} className="btn-green" data-testid="new-blog-post-btn">
          <Plus className="w-4 h-4" /> Nouvel article
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {posts.length === 0 ? (
          <div className="md:col-span-3 fe-card p-12 text-center text-fegray">Aucun article</div>
        ) : posts.map((p) => (
          <div key={p.id} className="fe-card overflow-hidden" data-testid={`admin-blog-${p.slug}`}>
            <div className="aspect-[16/10] overflow-hidden bg-fegray-light relative">
              <img src={p.cover} alt={p.title} className="w-full h-full object-cover" />
              <span className={`absolute top-3 right-3 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full ${p.published ? "bg-fegreen text-white" : "bg-gray-500 text-white"}`}>
                {p.published ? "Publié" : "Brouillon"}
              </span>
            </div>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-wider text-fegreen font-bold mb-2">{p.category} · {p.locality}</div>
              <h3 className="text-base font-bold text-fegray-dark line-clamp-2 mb-3">{p.title}</h3>
              <p className="text-xs text-fegray line-clamp-2 mb-4">{p.excerpt}</p>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => startEdit(p)} className="text-xs px-3 py-1.5 rounded-full bg-fegreen-light text-fegreen font-semibold hover:bg-fegreen hover:text-white transition">
                  Éditer
                </button>
                <button onClick={() => togglePublish(p)} className="text-xs px-3 py-1.5 rounded-full bg-fegray-light text-fegray font-semibold hover:bg-fegray hover:text-white transition flex items-center gap-1">
                  {p.published ? <><EyeOff className="w-3 h-3"/> Dépublier</> : <><Eye className="w-3 h-3"/> Publier</>}
                </button>
                <button onClick={() => remove(p.id)} className="text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-600 font-semibold hover:bg-red-500 hover:text-white transition">
                  <Trash2 className="w-3 h-3 inline" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", multiline = false, rows = 3, placeholder }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-fegray mb-2 font-semibold">{label}</label>
      {multiline ? (
        <textarea
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-fegray-dark text-sm focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none font-mono"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-fegray-dark text-sm focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
        />
      )}
    </div>
  );
}
